import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Sparkles, ShieldCheck, AlertTriangle, Loader2, Check, X } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Banner sticky + Dialog di review per allergeni HACCP (EU 1169/2011).
 * - Mostra "Da analizzare X / Da confermare Y" sopra la lista prodotti food.
 * - Bottone "✨ Analizza con AI" → POST /api/ingredienti/auto-allergeni-batch
 * - Bottone "Controlla X prodotti" → apre dialog con lista cliccabile per conferma
 *
 * Props:
 *   localeId: string (filtra per locale)
 *   onChanged: callback invocato dopo conferma/analisi (per refresh lista esterna)
 */
const AllergeniManager = ({ localeId, onChanged }) => {
  const { token } = useAuth();
  const [stato, setStato] = useState({ total: 0, verified: 0, da_analizzare: 0, da_confermare: 0 });
  const [metadata, setMetadata] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [items, setItems] = useState([]); // ingredienti da confermare
  const [loadingItems, setLoadingItems] = useState(false);
  const [savingIds, setSavingIds] = useState(new Set());

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchStato = useCallback(async () => {
    if (!token) return;
    try {
      const qs = localeId ? `?locale_id=${localeId}` : "";
      const res = await fetch(`${API}/api/ingredienti/allergeni/stato${qs}`, { headers: authHeaders });
      if (res.ok) setStato(await res.json());
    } catch (e) {
      console.warn("Stato allergeni fetch failed:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, localeId]);

  const fetchMetadata = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/allergeni/metadata`, { headers: authHeaders });
      if (res.ok) setMetadata(await res.json());
    } catch (e) {
      console.warn("Metadata fetch failed:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchStato();
    fetchMetadata();
  }, [fetchStato, fetchMetadata]);

  // ─── Analisi AI batch ────────────────────────────────────────────
  const handleAnalyzeBatch = async (forceReanalyze = false) => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/ingredienti/auto-allergeni-batch`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ locale_id: localeId, limit: 100, force_reanalyze: forceReanalyze }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Errore analisi AI");
        return;
      }
      toast.success(
        data.auto_confermati > 0
          ? `✨ ${data.analizzati} analizzati: ${data.auto_confermati} auto-confermati · ${data.da_confermare} da controllare`
          : `✨ Analizzati ${data.analizzati} prodotti — controllali`
      );
      await fetchStato();
      if (onChanged) onChanged();
      if (data.analizzati > 0) {
        await openReviewDialog();
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setAnalyzing(false);
    }
  };

  // ─── Carica ingredienti da confermare ────────────────────────────
  const openReviewDialog = async () => {
    setDialogOpen(true);
    setLoadingItems(true);
    try {
      const qs = localeId ? `?locale_id=${localeId}` : "";
      const res = await fetch(`${API}/api/ingredienti${qs}`, { headers: authHeaders });
      const all = await res.json();
      // Filtra: hanno allergeni_source ma allergeni_verified != true
      const daConfermare = (all || []).filter(
        (i) => i.allergeni_source && !i.allergeni_verified,
      );
      // Ordina: bassa confidence in cima (richiedono attenzione)
      daConfermare.sort((a, b) => (a.allergeni_confidence || 0) - (b.allergeni_confidence || 0));
      setItems(daConfermare.map((i) => ({
        ...i,
        allergeni_editing: [...(i.allergeni || [])],
      })));
    } catch (e) {
      toast.error("Errore caricamento lista");
    } finally {
      setLoadingItems(false);
    }
  };

  const toggleAllergene = (ingId, key) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id !== ingId
          ? it
          : {
              ...it,
              allergeni_editing: it.allergeni_editing.includes(key)
                ? it.allergeni_editing.filter((a) => a !== key)
                : [...it.allergeni_editing, key],
            },
      ),
    );
  };

  const confermaIngrediente = async (item) => {
    setSavingIds((prev) => new Set(prev).add(item.id));
    try {
      const res = await fetch(`${API}/api/ingredienti/${item.id}/allergeni`, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ allergeni: item.allergeni_editing }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore");
        return;
      }
      toast.success(`✓ ${item.nome} confermato`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      await fetchStato();
      if (onChanged) onChanged();
    } finally {
      setSavingIds((prev) => {
        const s = new Set(prev);
        s.delete(item.id);
        return s;
      });
    }
  };

  // ─── Render ──────────────────────────────────────────────────────
  const daControllare = stato.da_analizzare + stato.da_confermare;

  if (stato.total === 0) return null;

  return (
    <>
      {daControllare > 0 ? (
        <div
          className="rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="allergeni-banner"
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="bg-amber-100 rounded-full p-2 shrink-0">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-amber-900">
                Allergeni HACCP da controllare
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                {stato.da_analizzare > 0 && (
                  <span>
                    {stato.da_analizzare} prodotti da analizzare con AI
                  </span>
                )}
                {stato.da_analizzare > 0 && stato.da_confermare > 0 && " · "}
                {stato.da_confermare > 0 && (
                  <span>{stato.da_confermare} da confermare</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {stato.da_analizzare > 0 && (
              <Button
                size="sm"
                onClick={() => handleAnalyzeBatch(false)}
                disabled={analyzing}
                className="h-9 rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="btn-analyze-allergeni"
              >
                {analyzing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1.5" />
                    Analizza con AI
                  </>
                )}
              </Button>
            )}
            {stato.da_confermare > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={openReviewDialog}
                className="h-9 rounded-xl border-amber-400 text-amber-800 hover:bg-amber-100"
                data-testid="btn-review-allergeni"
              >
                Controlla {stato.da_confermare}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          className="rounded-2xl border border-emerald-300 bg-emerald-50 p-2.5 px-3 flex items-center gap-2"
          data-testid="allergeni-banner-ok"
        >
          <ShieldCheck className="h-4 w-4 text-emerald-700 shrink-0" />
          <p className="text-xs font-semibold text-emerald-900">
            Allergeni HACCP verificati su tutti i {stato.total} prodotti
          </p>
        </div>
      )}

      {/* ════ DIALOG REVIEW ════ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-600" />
              Controlla allergeni — proposte AI
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Clicca sugli allergeni per togliere/aggiungere, poi conferma. Le proposte AI sono basate sul nome del prodotto.
            </p>
          </DialogHeader>

          {loadingItems ? (
            <div className="py-12 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <ShieldCheck className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold">Tutto controllato!</p>
              <p className="text-xs mt-1">Nessun ingrediente da confermare.</p>
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="allergeni-review-list">
              {items.map((item) => {
                const attivi = item.allergeni_editing;
                const inattivi = metadata.filter((al) => !attivi.includes(al.key));
                return (
                  <div
                    key={item.id}
                    className="rounded-xl border border-slate-200 bg-white p-3 space-y-3"
                    data-testid={`allergeni-item-${item.id}`}
                  >
                    {/* Header con nome + bottone conferma */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{item.nome}</p>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          {attivi.length > 0 ? (
                            <span className="text-[11px] font-medium text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
                              🤖 AI: {attivi.length} allergen{attivi.length === 1 ? "e" : "i"} rilevat{attivi.length === 1 ? "o" : "i"}
                            </span>
                          ) : (
                            <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              🤖 AI: nessun allergene rilevato
                            </span>
                          )}
                          {item.allergeni_confidence < 0.7 && (
                            <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-300">
                              ⚠️ Confidenza bassa
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => confermaIngrediente(item)}
                        disabled={savingIds.has(item.id)}
                        className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                        data-testid={`btn-conferma-allergeni-${item.id}`}
                      >
                        {savingIds.has(item.id) ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <>
                            <Check className="h-3.5 w-3.5 mr-1" />
                            Conferma
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Zona 1: Allergeni ATTIVI (selezionati / rilevati AI) */}
                    {attivi.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-rose-600 mb-1.5">
                          Contiene
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {attivi.map((key) => {
                            const al = metadata.find((m) => m.key === key);
                            if (!al) return null;
                            return (
                              <button
                                key={al.key}
                                type="button"
                                onClick={() => toggleAllergene(item.id, al.key)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border-2 border-rose-500 bg-rose-100 text-rose-900 text-[11px] font-bold shadow-sm hover:bg-rose-200 transition-all"
                                title="Clicca per togliere"
                                data-testid={`pill-${al.key}-${item.id}`}
                              >
                                <span>{al.icon}</span>
                                <span>{al.label}</span>
                                <X className="h-3 w-3 ml-0.5" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Zona 2: Allergeni DISPONIBILI (non selezionati) */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
                        {attivi.length > 0 ? "Aggiungi altri" : "Tocca per aggiungere"}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {inattivi.map((al) => (
                          <button
                            key={al.key}
                            type="button"
                            onClick={() => toggleAllergene(item.id, al.key)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-500 text-[11px] hover:bg-slate-100 hover:border-slate-300 transition-all"
                            data-testid={`pill-${al.key}-${item.id}`}
                          >
                            <span className="opacity-60">{al.icon}</span>
                            <span>{al.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {items.length > 0 && (
              <Button
                variant="outline"
                onClick={() => { setDialogOpen(false); handleAnalyzeBatch(true); }}
                disabled={analyzing}
                className="border-amber-400 text-amber-800 hover:bg-amber-50"
                data-testid="btn-rianalize-allergeni"
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                Ri-analizza tutto con AI
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AllergeniManager;
